import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  CreateTaskInput,
  ProjectDetailRecord,
  ProjectRuntimeOperationRecord,
  ProjectStage,
  ProjectStatus,
  TaskRecord,
  TaskStatus,
  UpdateTaskInput,
  WorkspaceSettingsRecord
} from '@shared/types';
import { subscribeToShellDeleteRequests } from '@renderer/app/events/shell-delete-events';
import { useShellStore } from '@renderer/app/state/shell-store';
import { EditorBodyLayout, EditorHeroCard, EditorPageLayout, EditorSectionCard } from '@renderer/shared/components/editor-scaffold';
import { Icon } from '@renderer/shared/components/icons';
import {
  EditorTabs,
  EmptyState,
  OverflowMenuButton,
  ProgressBar,
  SelectField,
  Spinner,
  StatusBadge,
  TextAreaField,
  TextField,
  ValuePair
} from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { useUiStore } from '@renderer/stores/ui-store';
import { getWorkspaceSettings } from '@renderer/domains/settings/settings-repository';
import { ProjectResourcesModal } from '@renderer/domains/resources/components/project-resources-modal';
import { TaskEditModal } from '@renderer/domains/tasks/components/task-edit-modal';
import { createTask, deleteTasks, updateTask } from '@renderer/domains/tasks/tasks-repository';
import { useRegisterEditorSaveHandler } from '@renderer/shared/hooks/use-register-editor-save-handler';
import { areDraftsEqual } from '@renderer/shared/utils/draft-equality';
import { getAssetPreviewUrl } from '@renderer/shared/utils/asset-url';
import { ProjectBuilderWorkspace } from './builder/project-builder-workspace';
import { useProjectBuilderStore } from './builder/project-builder-store';
import styles from './project-editor-screen.module.css';
import {
  attachProjectBrandingAsset,
  deleteProjects,
  getProjectById,
  getProjectRuntimeStatus,
  openProjectLocalUrl,
  provisionProjectRuntime,
  updateProject,
  updateWordPressAdminCredentials,
  updateProjectRuntime
} from './projects-repository';
import { selectProjectEditorModel } from './projects-selectors';
import { useProjectsViewStore } from './projects-view-store';

interface ProjectDraft {
  name: string;
  summary: string;
  status: ProjectStatus;
  stage: ProjectStage;
  primaryDomain: string;
  localDomain: string;
  deadlineAt: string;
  assigneeName: string;
  tags: string;
}

type RuntimeActionKind = 'install' | 'update';

function formatDateInputValue(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function toIsoDate(value: string): string | null {
  return value ? `${value}T12:00:00.000Z` : null;
}

function toDraft(detail: ProjectDetailRecord): ProjectDraft {
  return {
    name: detail.project.name,
    summary: detail.project.summary ?? '',
    status: detail.project.status,
    stage: detail.project.stage,
    primaryDomain: detail.project.primaryDomain ?? '',
    localDomain: detail.project.localDomain ?? '',
    deadlineAt: formatDateInputValue(detail.project.deadlineAt),
    assigneeName: detail.project.assigneeName ?? '',
    tags: detail.project.tags.join(', ')
  };
}

function formatRuntimeValue(value: string | null | undefined, fallback: string): string {
  return value && value.length > 0 ? value : fallback;
}

function formatRuntimeDate(value: string | null | undefined, locale: string): string | null {
  if (!value) {
    return null;
  }

  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

function cn(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

export function ProjectEditorScreen(): JSX.Element {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { t, locale } = useI18n();
  const addToast = useUiStore((state) => state.addToast);
  const setRouteDetailTitle = useShellStore((state) => state.setRouteDetailTitle);
  const { editorTab, setEditorTab } = useProjectsViewStore();
  const isBuilderMaximized = useProjectBuilderStore((state) => state.isMaximized);
  const [detail, setDetail] = useState<ProjectDetailRecord | null>(null);
  const [draft, setDraft] = useState<ProjectDraft | null>(null);
  const [workspaceSettings, setWorkspaceSettings] = useState<WorkspaceSettingsRecord | null>(null);
  const [runtimeStatus, setRuntimeStatus] = useState<ProjectRuntimeOperationRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [runtimeError, setRuntimeError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isResourcesModalOpen, setIsResourcesModalOpen] = useState(false);
  const [taskModalState, setTaskModalState] = useState<{ mode: 'create' } | { mode: 'edit'; taskId: string } | null>(null);
  const [taskMutationError, setTaskMutationError] = useState<string | null>(null);
  const [wordpressAdminDraft, setWordpressAdminDraft] = useState({ username: '', password: '' });
  const [wordpressAdminError, setWordpressAdminError] = useState<string | null>(null);
  const [isSavingWordpressAdmin, setIsSavingWordpressAdmin] = useState(false);
  const [isRunningRuntimeAction, setIsRunningRuntimeAction] = useState(false);
  const [runtimeActionKind, setRuntimeActionKind] = useState<RuntimeActionKind | null>(null);
  const [runtimePollingProjectId, setRuntimePollingProjectId] = useState<string | null>(null);
  const [uploadingBrandingKind, setUploadingBrandingKind] = useState<'logo' | 'icon' | null>(null);
  const builderFlushRef = useRef<(() => Promise<void>) | null>(null);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const iconInputRef = useRef<HTMLInputElement | null>(null);
  const savedDraft = useMemo(() => (detail ? toDraft(detail) : null), [detail]);

  useEffect(() => {
    if (!projectId) {
      setError('Missing project id.');
      return;
    }

    const currentProjectId: string = projectId;

    let active = true;

    async function loadProject(): Promise<void> {
      try {
        const [nextDetail, nextWorkspaceSettings, nextRuntimeStatus] = await Promise.all([
          getProjectById(currentProjectId),
          getWorkspaceSettings(),
          getProjectRuntimeStatus(currentProjectId)
        ]);

        if (!active) {
          return;
        }

        if (!nextDetail) {
          setError('Project not found.');
          return;
        }

        setDetail(nextDetail);
        setDraft(toDraft(nextDetail));
        setWorkspaceSettings(nextWorkspaceSettings);
        setRuntimeStatus(nextRuntimeStatus);
        setRouteDetailTitle(nextDetail.project.name);
        setWordpressAdminDraft({ username: nextDetail.project.wordpressAdminUsername ?? '', password: '' });
      } catch (nextError) {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Project unavailable.');
        }
      }
    }

    void loadProject();

    return () => {
      active = false;
      setRouteDetailTitle(null);
    };
  }, [projectId, setRouteDetailTitle]);

  useEffect(
    () =>
      subscribeToShellDeleteRequests((detailRequest) => {
        if (detailRequest.domain !== 'projects' || !detail || !detailRequest.ids.includes(detail.project.id)) {
          return;
        }

        void deleteProjects({ ids: [detail.project.id] }).then(() => navigate('/projects'));
      }),
    [detail, navigate]
  );

  useRegisterEditorSaveHandler(
    detail && draft && workspaceSettings && savedDraft
      ? async () => {
          await builderFlushRef.current?.();

          if (isSaving) {
            return;
          }

          const nextIsDirty = !areDraftsEqual(draft, savedDraft);
          if (!nextIsDirty) {
            return;
          }

          await handleSave();
        }
      : null
  );

  useEffect(() => {
    if (!runtimePollingProjectId) {
      return;
    }

    let active = true;

    const refreshRuntimeStatus = async () => {
      try {
        const nextRuntimeStatus = await getProjectRuntimeStatus(runtimePollingProjectId);
        if (active) {
          setRuntimeStatus(nextRuntimeStatus);
        }
      } catch {
        return;
      }
    };

    void refreshRuntimeStatus();
    const interval = window.setInterval(() => {
      void refreshRuntimeStatus();
    }, 900);

    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [runtimePollingProjectId]);

  if (error) {
    return (
      <div className="av-page">
        <EmptyState title={t('projects.title')} description={error} />
      </div>
    );
  }

  if (!detail || !draft || !workspaceSettings) {
    return <div className="av-page" />;
  }

  const currentDetail = detail;
  const currentDraft = draft;
  const model = selectProjectEditorModel(currentDetail, t);
  const projectIdToDelete = currentDetail.project.id;
  const isDirty = savedDraft ? !areDraftsEqual(currentDraft, savedDraft) : false;
  const statusOptions = (['active', 'in_progress', 'pending', 'complete', 'archived'] satisfies ProjectStatus[]).map((value) => ({
    value,
    label: t(`enums.projectStatus.${value}`)
  }));
  const stageOptions = (['brief', 'branding', 'build', 'review', 'launch', 'support'] satisfies ProjectStage[]).map((value) => ({
    value,
    label: t(`enums.projectStage.${value}`)
  }));
  const taskStatusOptions = (['backlog', 'todo', 'in_progress', 'done', 'archived'] satisfies TaskStatus[]).map((value) => ({
    value,
    label: t(`enums.taskStatus.${value}`)
  }));
  const taskProjectOptions = [{ value: currentDetail.project.id, label: currentDetail.project.name }];
  const activeTaskRecord =
    taskModalState?.mode === 'edit'
      ? currentDetail.tasks.find((task) => task.id === taskModalState.taskId) ?? null
      : null;
  const newProjectTaskDraft: TaskRecord = {
    id: 'new-project-task',
    createdAt: currentDetail.project.updatedAt,
    updatedAt: currentDetail.project.updatedAt,
    deletedAt: null,
    archivedAt: null,
    title: '',
    description: null,
    status: 'todo',
    projectId: currentDetail.project.id,
    customerId: currentDetail.project.customerId,
    assigneeId: null,
    dueAt: null,
    startedAt: null,
    completedAt: null,
    boardOrder: currentDetail.tasks.length,
    taskLabelIds: [],
    priority: 'medium'
  };
  const runtimeRecord = runtimeStatus?.runtime ?? currentDetail.runtime;
  const runtimeWarnings = runtimeStatus?.warnings ?? [];
  const runtimeMessage = runtimeStatus?.message ?? t(`project.runtime.${currentDetail.project.deploymentState}`);
  const runtimeStepLabel = runtimeStatus?.currentStepLabel ? t(runtimeStatus.currentStepLabel) : null;
  const runtimeOperationKindLabel = runtimeStatus?.operationKind ? t(`project.runtime.operationKinds.${runtimeStatus.operationKind}`) : null;
  const runtimeProgressValue = runtimeStatus?.progressPercent ?? 0;
  const localDomainPlaceholder = `${currentDetail.project.slug}${workspaceSettings.localEnvironment.defaultLocalDomainSuffix}`;
  const hasRuntime = Boolean(runtimeRecord);
  const isRuntimeBusy = isRunningRuntimeAction || runtimeStatus?.status === 'updating';
  const runtimeStartedAt = formatRuntimeDate(runtimeStatus?.startedAt, locale) ?? t('common.notSet');
  const runtimeFinishedAt = formatRuntimeDate(runtimeStatus?.finishedAt, locale) ?? t('common.notSet');
  const installButtonLabel =
    isRunningRuntimeAction && runtimeActionKind === 'install' ? t('project.actions.installingSite') : t('project.actions.installSite');
  const updateButtonLabel =
    isRunningRuntimeAction && runtimeActionKind === 'update' ? t('project.actions.updatingSite') : t('project.actions.updateSite');
  const projectLogoAsset = currentDetail.assets.find((asset) => asset.id === currentDetail.project.logoAssetId) ?? null;
  const projectIconAsset = currentDetail.assets.find((asset) => asset.id === currentDetail.project.iconAssetId) ?? null;
  const projectLogoPreviewUrl = getAssetPreviewUrl(projectLogoAsset?.id);
  const projectIconPreviewUrl = getAssetPreviewUrl(projectIconAsset?.id);
  const heroBrandingPreviewUrl = projectIconPreviewUrl ?? projectLogoPreviewUrl;

  async function refreshProjectDetail(): Promise<void> {
    if (!currentDetail) {
      return;
    }

    const nextDetail = await getProjectById(currentDetail.project.id);

    if (nextDetail) {
      setDetail(nextDetail);
      setDraft(toDraft(nextDetail));
      setRouteDetailTitle(nextDetail.project.name);
    }
  }

  async function handleDeleteProject(): Promise<void> {
    if (!window.confirm(t('common.confirmDeleteSoft'))) {
      return;
    }

    await deleteProjects({ ids: [projectIdToDelete] });
    navigate('/projects');
  }

  async function handleSave(): Promise<void> {
    if (!currentDetail || !currentDraft) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const detailResponse = await updateProject({
        id: currentDetail.project.id,
        name: currentDraft.name.trim(),
        summary: currentDraft.summary,
        status: currentDraft.status,
        stage: currentDraft.stage,
        primaryDomain: currentDraft.primaryDomain.trim() ? currentDraft.primaryDomain.trim() : null,
        localDomain: currentDraft.localDomain.trim() ? currentDraft.localDomain.trim() : null,
        deadlineAt: toIsoDate(currentDraft.deadlineAt),
        assigneeName: currentDraft.assigneeName.trim() ? currentDraft.assigneeName.trim() : null,
        tags: currentDraft.tags
          .split(',')
          .map((entry) => entry.trim())
          .filter(Boolean)
      });

      setDetail(detailResponse);
      setDraft(toDraft(detailResponse));
      setRouteDetailTitle(detailResponse.project.name);
      setRuntimeStatus(await getProjectRuntimeStatus(detailResponse.project.id));
      addToast({
        title: t('project.notifications.saveSuccessTitle'),
        message: t('project.notifications.saveSuccessMessage'),
        tone: 'success'
      });
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Save failed.';
      setSaveError(message);
      addToast({
        title: t('project.notifications.saveErrorTitle'),
        message,
        tone: 'danger'
      });
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel(): void {
    if (!currentDetail) {
      return;
    }

    setSaveError(null);
    setDraft(toDraft(currentDetail));
  }

  async function handleCreateProjectTask(payload: CreateTaskInput): Promise<void> {
    setTaskMutationError(null);

    try {
      await createTask({
        ...payload,
        projectId: currentDetail.project.id,
        customerId: currentDetail.project.customerId
      });
      await refreshProjectDetail();
      addToast({
        title: t('project.notifications.taskSavedTitle'),
        message: t('project.notifications.taskCreatedMessage'),
        tone: 'success'
      });
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Task creation failed.';
      setTaskMutationError(message);
      throw nextError;
    }
  }

  async function handleUpdateProjectTask(payload: UpdateTaskInput): Promise<void> {
    setTaskMutationError(null);

    try {
      await updateTask(payload);
      await refreshProjectDetail();
      addToast({
        title: t('project.notifications.taskSavedTitle'),
        message: t('project.notifications.taskUpdatedMessage'),
        tone: 'success'
      });
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Task update failed.';
      setTaskMutationError(message);
      throw nextError;
    }
  }

  async function handleDeleteProjectTask(taskId: string): Promise<void> {
    if (!window.confirm(t('tasks.confirmDelete'))) {
      return;
    }

    setTaskMutationError(null);

    try {
      await deleteTasks({ ids: [taskId] });
      await refreshProjectDetail();
      addToast({
        title: t('project.notifications.taskDeletedTitle'),
        message: t('project.notifications.taskDeletedMessage'),
        tone: 'success'
      });
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Task delete failed.';
      setTaskMutationError(message);
      addToast({
        title: t('project.notifications.taskDeleteErrorTitle'),
        message,
        tone: 'danger'
      });
    }
  }

  async function handleBrandingFileSelected(kind: 'logo' | 'icon', file: File | null): Promise<void> {
    if (!currentDetail || !file) {
      return;
    }

    const isSvg =
      file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
    if (!isSvg) {
      addToast({
        title: t('project.notifications.brandingUploadErrorTitle'),
        message: t('project.notifications.brandingUploadInvalidFile'),
        tone: 'danger'
      });
      return;
    }

    try {
      setUploadingBrandingKind(kind);
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      const nextDetail = await attachProjectBrandingAsset({
        projectId: currentDetail.project.id,
        kind,
        item: {
          fileName: file.name,
          mimeType: 'image/svg+xml',
          bytes
        }
      });
      setDetail(nextDetail);
      setDraft(toDraft(nextDetail));
      setRuntimeStatus(await getProjectRuntimeStatus(nextDetail.project.id));
      addToast({
        title: t('project.notifications.brandingUploadSuccessTitle'),
        message:
          kind === 'logo'
            ? t('project.notifications.brandingUploadSuccessMessageLogo')
            : t('project.notifications.brandingUploadSuccessMessageIcon'),
        tone: 'success'
      });
    } catch (uploadError) {
      const message = uploadError instanceof Error ? uploadError.message : String(uploadError);
      addToast({
        title: t('project.notifications.brandingUploadErrorTitle'),
        message,
        tone: 'danger'
      });
    } finally {
      setUploadingBrandingKind(null);
    }
  }

  async function handleRuntimeAction(action: RuntimeActionKind): Promise<void> {
    if (!currentDetail || !currentDraft) {
      return;
    }

    await useShellStore.getState().editorSaveHandler?.();
    await refreshProjectDetail();

    setIsRunningRuntimeAction(true);
    setRuntimeActionKind(action);
    setRuntimePollingProjectId(currentDetail.project.id);
    setRuntimeError(null);
    const startedAt = new Date().toISOString();
    const startMessage =
      action === 'install' ? t('project.notifications.installStartedMessage') : t('project.notifications.updateStartedMessage');
    const startTitle =
      action === 'install' ? t('project.notifications.installStartedTitle') : t('project.notifications.updateStartedTitle');

    setRuntimeStatus({
      projectId: currentDetail.project.id,
      status: 'updating',
      message: startMessage,
      warnings: [],
      runtime: runtimeRecord ?? currentDetail.runtime,
      localUrl: runtimeStatus?.localUrl ?? null,
      operationKind: action === 'install' ? 'provision' : 'update',
      currentStep: action === 'install' ? 'prepare_context' : 'prepare_update',
      currentStepLabel: action === 'install' ? 'project.runtime.steps.prepareContext' : 'project.runtime.steps.prepareUpdate',
      progressPercent: 8,
      startedAt,
      finishedAt: null
    });
    addToast({
      title: startTitle,
      message: startMessage,
      tone: 'neutral'
    });

    try {
      const payload = {
        projectId: currentDetail.project.id,
        localDomain: currentDraft.localDomain.trim() ? currentDraft.localDomain.trim() : null
      };
      const nextRuntimeStatus =
        action === 'install' ? await provisionProjectRuntime(payload) : await updateProjectRuntime(payload);

      setRuntimeStatus(nextRuntimeStatus);
      await refreshProjectDetail();
      addToast({
        title: t(
          nextRuntimeStatus.status === 'ready'
            ? action === 'install'
              ? 'project.notifications.installSuccessTitle'
              : 'project.notifications.updateSuccessTitle'
            : action === 'install'
              ? 'project.notifications.installWarningTitle'
              : 'project.notifications.updateWarningTitle'
        ),
        message: nextRuntimeStatus.message,
        tone: nextRuntimeStatus.status === 'ready' ? 'success' : 'warning'
      });
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Runtime action failed.';
      setRuntimeError(message);
      try {
        setRuntimeStatus(await getProjectRuntimeStatus(currentDetail.project.id));
      } catch {
        // Keep the existing runtime status if the refresh fails.
      }
      addToast({
        title: t(action === 'install' ? 'project.notifications.installErrorTitle' : 'project.notifications.updateErrorTitle'),
        message,
        tone: 'danger'
      });
    } finally {
      setIsRunningRuntimeAction(false);
      setRuntimeActionKind(null);
      setRuntimePollingProjectId(null);
    }
  }

  async function handleSaveWordPressAdminCredentials(): Promise<void> {
    if (!detail) {
      return;
    }

    setIsSavingWordpressAdmin(true);
    setWordpressAdminError(null);

    try {
      const trimmedUsername = wordpressAdminDraft.username.trim();
      const trimmedPassword = wordpressAdminDraft.password.trim();

      const nextDetail = await updateWordPressAdminCredentials({
        projectId: detail.project.id,
        adminUsername: trimmedUsername ? trimmedUsername : null,
        ...(trimmedPassword ? { adminPassword: trimmedPassword } : {})
      });

      setDetail(nextDetail);
      setWordpressAdminDraft({ username: nextDetail.project.wordpressAdminUsername ?? '', password: '' });
      addToast({
        title: t('project.notifications.credentialsSavedTitle'),
        message: t('project.notifications.credentialsSavedMessage'),
        tone: 'success'
      });
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Credentials could not be saved.';
      setWordpressAdminError(message);
      addToast({
        title: t('project.notifications.credentialsSaveErrorTitle'),
        message,
        tone: 'danger'
      });
    } finally {
      setIsSavingWordpressAdmin(false);
    }
  }

  async function handleClearWordPressAdminPasswordOverride(): Promise<void> {
    if (!detail) {
      return;
    }

    setIsSavingWordpressAdmin(true);
    setWordpressAdminError(null);

    try {
      const nextDetail = await updateWordPressAdminCredentials({
        projectId: detail.project.id,
        adminPassword: null
      });

      setDetail(nextDetail);
      setWordpressAdminDraft((current) => ({ ...current, password: '' }));
      addToast({
        title: t('project.notifications.credentialsSavedTitle'),
        message: t('project.notifications.credentialsSavedMessage'),
        tone: 'success'
      });
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Credentials could not be saved.';
      setWordpressAdminError(message);
      addToast({
        title: t('project.notifications.credentialsSaveErrorTitle'),
        message,
        tone: 'danger'
      });
    } finally {
      setIsSavingWordpressAdmin(false);
    }
  }

  async function handleOpenLocal(): Promise<void> {
    if (!currentDetail) {
      return;
    }

    setRuntimeError(null);

    try {
      await openProjectLocalUrl(currentDetail.project.id);
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Local website could not be opened.';
      setRuntimeError(message);
      addToast({
        title: t('project.notifications.openLocalErrorTitle'),
        message,
        tone: 'danger'
      });
    }
  }

  function handleOpenExternal(url: string | null): void {
    if (!url) {
      return;
    }

    const normalizedUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
    window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
  }

  const builderWorkspace = (
    <ProjectBuilderWorkspace
      builderFlushRef={builderFlushRef}
      detail={detail}
      onDetailChange={(nextDetail) => {
        setDetail(nextDetail);
        setDraft((prevDraft) => {
          if (!detail || !prevDraft) {
            return toDraft(nextDetail);
          }

          const wasProjectDirty = !areDraftsEqual(prevDraft, toDraft(detail));
          if (!wasProjectDirty) {
            return toDraft(nextDetail);
          }

          const synced = toDraft(nextDetail);
          return {
            ...synced,
            name: prevDraft.name,
            summary: prevDraft.summary,
            status: prevDraft.status,
            stage: prevDraft.stage,
            primaryDomain: prevDraft.primaryDomain,
            localDomain: prevDraft.localDomain,
            deadlineAt: prevDraft.deadlineAt,
            assigneeName: prevDraft.assigneeName,
            tags: prevDraft.tags
          };
        });
        setRouteDetailTitle(nextDetail.project.name);
      }}
    />
  );

  if (editorTab === 'builder' && isBuilderMaximized) {
    return (
      <div className={cn('av-page', styles.pageBuilderImmersive)}>
        {saveError ? <div className="av-inline-error">{saveError}</div> : null}
        {builderWorkspace}
      </div>
    );
  }

  return (
    <>
      <EditorPageLayout
        hero={
          <EditorHeroCard
          actions={
            <>
              <button className="av-secondary-button" type="button" onClick={() => void handleDeleteProject()}>
                <Icon name="trash" />
                <span>{t('shell.delete')}</span>
              </button>
              <OverflowMenuButton />
            </>
          }
          className="av-project-header-card"
        >
            <div className="av-editor-hero-card__content">
              <div className="av-editor-hero-card__identity">
                <div className="av-editor-hero-card__icon">
                  {heroBrandingPreviewUrl ? <img src={heroBrandingPreviewUrl} alt="" /> : <Icon name="wordpress" />}
                </div>
                <div className="av-editor-hero-card__identity-copy">
                  <h2>{detail.project.name}</h2>
                <div className="av-editor-hero-card__meta">
                  <StatusBadge label={model.statusLabel} tone="primary" />
                </div>
              </div>
            </div>

            <div className="av-editor-hero-card__endpoints">
              {model.endpoints.map((endpoint) => (
                <div key={endpoint.key} className="av-endpoint-row">
                  <Icon name={endpoint.icon} />
                  <span>{endpoint.value}</span>
                </div>
              ))}
            </div>

            <div className="av-editor-hero-card__actions">
              <button className="av-secondary-button" type="button" onClick={() => setEditorTab('builder')}>
                <Icon name="builder" />
                <span>{t('project.actions.openBuilder')}</span>
              </button>
              <button
                className="av-secondary-button"
                type="button"
                onClick={() => void handleRuntimeAction('install')}
                disabled={isRunningRuntimeAction}
              >
                {isRunningRuntimeAction && runtimeActionKind === 'install' ? <Spinner size="small" /> : <Icon name="plus" />}
                <span>{installButtonLabel}</span>
              </button>
              <button
                className="av-primary-button av-primary-button--dark"
                type="button"
                onClick={() => void handleRuntimeAction('update')}
                disabled={isRunningRuntimeAction}
              >
                {isRunningRuntimeAction && runtimeActionKind === 'update' ? <Spinner size="small" /> : <Icon name="update" />}
                <span>{updateButtonLabel}</span>
              </button>
            </div>
          </div>
        </EditorHeroCard>
      }
        heroAside={
          <EditorSectionCard title={t('project.sections.websiteControl')} actions={<OverflowMenuButton />}>
            <div className="av-control-panel">
              {detail.project.primaryDomain ? (
                <button className="av-secondary-button" type="button" onClick={() => handleOpenExternal(detail.project.primaryDomain)}>
                  <Icon name="website" />
                  <span>{t('project.actions.openPrimaryDomain')}</span>
                </button>
              ) : null}
              {detail.project.localDomain ? (
                <button className="av-secondary-button" type="button" onClick={() => void handleOpenLocal()} disabled={isRuntimeBusy}>
                  <Icon name="monitor" />
                  <span>{t('project.actions.openLocalDomain')}</span>
                </button>
              ) : null}
            </div>
            {runtimeError ? <div className="av-inline-error">{runtimeError}</div> : null}
          </EditorSectionCard>
        }
        tabs={
          <EditorTabs
            tabs={[
              { id: 'general', label: t('project.tabs.general'), icon: 'projects' },
              { id: 'builder', label: t('project.tabs.builder'), icon: 'builder' },
              { id: 'tasks', label: t('project.tabs.tasks'), icon: 'tasks' }
            ]}
            value={editorTab}
            onChange={setEditorTab}
          />
        }
      >
        <EditorBodyLayout
          main={
            <>
              {saveError ? <div className="av-inline-error">{saveError}</div> : null}
              {editorTab === 'general' ? (
                <>
                <EditorSectionCard
                  title={t('project.sections.branding')}
                  actions={
                    <div className="av-inline-metadata">
                      <button className="av-secondary-button" type="button" onClick={handleCancel} disabled={!isDirty || isSaving}>
                        {t('common.cancel')}
                      </button>
                      <button className="av-primary-button" type="button" onClick={() => void handleSave()} disabled={!isDirty || isSaving}>
                        {t('common.applyChanges')}
                      </button>
                    </div>
                  }
                >
                  <div className="av-form-grid">
                    <label className="av-form-field">
                      <span>{t('project.fields.projectName')}</span>
                      <TextField
                        value={draft.name}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, name: value } : current))}
                      />
                    </label>
                    <div className="av-form-field">
                      <span>{t('project.fields.customer')}</span>
                      <Link className="av-inline-link av-inline-link--field" to={`/customers/${detail.project.customerId}`}>
                        <span>{model.customerName}</span>
                        <Icon name="chevron" />
                      </Link>
                    </div>
                    <label className="av-form-field">
                      <span>{t('project.fields.status')}</span>
                      <SelectField
                        value={draft.status}
                        options={statusOptions}
                        onChange={(event) =>
                          setDraft((current) => (current ? { ...current, status: event.target.value as ProjectStatus } : current))
                        }
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('project.summary.stage')}</span>
                      <SelectField
                        value={draft.stage}
                        options={stageOptions}
                        onChange={(event) =>
                          setDraft((current) => (current ? { ...current, stage: event.target.value as ProjectStage } : current))
                        }
                      />
                    </label>
                  </div>

                  <div className="av-form-field">
                    <span>{t('project.summary.deployment')}</span>
                    <div className="av-readonly-text-block">{model.deploymentLabel}</div>
                  </div>

                  <div className="av-form-field">
                    <span>{t('project.summary.progress')}</span>
                    <ProgressBar value={detail.project.progressPercent ?? 0} />
                  </div>

                  <div className="av-form-field">
                    <span>{t('project.fields.summary')}</span>
                    <TextAreaField
                      rows={4}
                      value={draft.summary}
                      onValueChange={(value) => setDraft((current) => (current ? { ...current, summary: value } : current))}
                    />
                  </div>

                  <div className="av-form-grid">
                    <label className="av-form-field">
                      <span>{t('project.fields.primaryDomain')}</span>
                      <TextField
                        placeholder="https://example.ro"
                        value={draft.primaryDomain}
                        onValueChange={(value) =>
                          setDraft((current) => (current ? { ...current, primaryDomain: value } : current))
                        }
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('project.fields.localDomain')}</span>
                      <TextField
                        placeholder={localDomainPlaceholder}
                        value={draft.localDomain}
                        onValueChange={(value) =>
                          setDraft((current) => (current ? { ...current, localDomain: value } : current))
                        }
                      />
                    </label>
                  </div>

                  <div className="av-form-grid">
                    <label className="av-form-field">
                      <span>{t('projects.bulk.fields.deadline')}</span>
                      <TextField
                        type="date"
                        value={draft.deadlineAt}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, deadlineAt: value } : current))}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('projects.bulk.fields.assignee')}</span>
                      <TextField
                        placeholder={t('projects.bulk.assigneePlaceholder')}
                        value={draft.assigneeName}
                        onValueChange={(value) =>
                          setDraft((current) => (current ? { ...current, assigneeName: value } : current))
                        }
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('projects.bulk.fields.tags')}</span>
                      <TextField
                        placeholder={t('projects.bulk.tagsPlaceholder')}
                        value={draft.tags}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, tags: value } : current))}
                      />
                    </label>
                  </div>

                  <div className={styles.brandingRow}>
                    <input
                      className={styles.fileInput}
                      ref={logoInputRef}
                      type="file"
                      accept="image/svg+xml,.svg"
                      onChange={(event) => {
                        const [file] = Array.from(event.target.files ?? []);
                        void handleBrandingFileSelected('logo', file ?? null);
                        event.target.value = '';
                      }}
                    />
                    <input
                      className={styles.fileInput}
                      ref={iconInputRef}
                      type="file"
                      accept="image/svg+xml,.svg"
                      onChange={(event) => {
                        const [file] = Array.from(event.target.files ?? []);
                        void handleBrandingFileSelected('icon', file ?? null);
                        event.target.value = '';
                      }}
                    />
                    <button
                      className={cn(styles.brandingPreviewCard, styles.brandingPreviewCardUpload)}
                      type="button"
                      disabled={uploadingBrandingKind !== null}
                      aria-label={t('project.actions.uploadLogo')}
                      onClick={() => logoInputRef.current?.click()}
                    >
                      <span>{t('project.fields.logo')}</span>
                      <div className={styles.brandingPreviewCardFrame}>
                        {uploadingBrandingKind === 'logo' ? (
                          <Spinner size="small" label={t('project.actions.uploadLogo')} />
                        ) : projectLogoPreviewUrl ? (
                          <img src={projectLogoPreviewUrl} alt="" />
                        ) : (
                          <Icon name="images" />
                        )}
                      </div>
                      <strong>{projectLogoAsset?.name ?? t('project.branding.emptyAsset')}</strong>
                    </button>
                    <button
                      className={cn(styles.brandingPreviewCard, styles.brandingPreviewCardUpload)}
                      type="button"
                      disabled={uploadingBrandingKind !== null}
                      aria-label={t('project.actions.uploadIcon')}
                      onClick={() => iconInputRef.current?.click()}
                    >
                      <span>{t('project.fields.icon')}</span>
                      <div className={styles.brandingPreviewCardFrame}>
                        {uploadingBrandingKind === 'icon' ? (
                          <Spinner size="small" label={t('project.actions.uploadIcon')} />
                        ) : projectIconPreviewUrl ? (
                          <img src={projectIconPreviewUrl} alt="" />
                        ) : (
                          <Icon name="icon" />
                        )}
                      </div>
                      <strong>{projectIconAsset?.name ?? t('project.branding.emptyAsset')}</strong>
                    </button>
                    <button
                      className={cn('av-secondary-button', styles.brandingRowLink)}
                      type="button"
                      onClick={() => setIsResourcesModalOpen(true)}
                    >
                      <Icon name="resources" /> {t('project.actions.openResources')}
                    </button>
                  </div>
                </EditorSectionCard>

                <EditorSectionCard title={t('project.sections.runtime')} actions={<OverflowMenuButton />}>
                  <div className="av-summary-stack">
                    <ValuePair label={t('project.fields.runtimeStatus')} value={t(`enums.deploymentState.${runtimeStatus?.status ?? detail.project.deploymentState}`)} />
                    <ValuePair label={t('project.fields.siteRootPath')} value={runtimeRecord?.siteRootPath ?? formatRuntimeValue(null, t('common.notSet'))} />
                    <ValuePair label={t('project.fields.documentRootPath')} value={runtimeRecord?.documentRootPath ?? formatRuntimeValue(null, t('common.notSet'))} />
                    <ValuePair
                      label={t('project.fields.databaseName')}
                      value={runtimeRecord?.databaseName ?? t('project.runtime.noDatabase')}
                    />
                    <ValuePair
                      label={t('project.fields.databaseTablePrefix')}
                      value={runtimeRecord?.databaseTablePrefix ?? t('project.runtime.noDatabase')}
                    />
                    <ValuePair
                      label={t('project.fields.httpsEnabled')}
                      value={runtimeRecord?.httpsEnabled ? t('project.runtime.enabled') : t('project.runtime.disabled')}
                    />
                    <ValuePair
                      label={t('project.fields.lastUpdatedAt')}
                      value={formatRuntimeDate(runtimeRecord?.lastUpdatedAt, locale) ?? t('common.notSet')}
                    />
                    <ValuePair
                      label={t('project.fields.builderRevision')}
                      value={runtimeRecord?.builderRevision ?? t('common.notSet')}
                    />
                    <ValuePair
                      label={t('project.fields.runtimeOperation')}
                      value={runtimeOperationKindLabel ?? t('common.notSet')}
                    />
                    <ValuePair
                      label={t('project.fields.runtimeStep')}
                      value={runtimeStepLabel ?? t('common.notSet')}
                    />
                    <ValuePair label={t('project.fields.runtimeStartedAt')} value={runtimeStartedAt} />
                    <ValuePair label={t('project.fields.runtimeFinishedAt')} value={runtimeFinishedAt} />
                    <div className="av-readonly-text-block">{hasRuntime ? runtimeMessage : t('project.runtime.notProvisioned')}</div>
                    {isRuntimeBusy ? (
                      <div className={styles.runtimeProgress}>
                        <div className={styles.runtimeProgressHeader}>
                          <Spinner size="small" label={runtimeStepLabel ?? runtimeMessage} />
                          <span className="av-summary-label">{t('project.summary.runtimeProgress')}</span>
                        </div>
                        <ProgressBar value={runtimeProgressValue} />
                      </div>
                    ) : null}
                    {runtimeWarnings.length > 0 ? (
                      <ul className="av-summary-list">
                        {runtimeWarnings.map((warning) => (
                          <li key={warning}>
                            <strong>{t('enums.deploymentState.warning')}</strong>
                            <span>{warning}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </EditorSectionCard>

                <EditorSectionCard title={t('project.sections.social')} actions={<OverflowMenuButton />}>
                  <div className="av-social-grid">
                    <div className="av-social-card"><Icon name="facebook" /> {t('project.social.facebook')}</div>
                    <div className="av-social-card"><Icon name="instagram" /> {t('project.social.instagram')}</div>
                    <div className="av-social-card"><Icon name="googleBusiness" /> {t('project.social.googleBusiness')}</div>
                    <div className="av-social-card"><Icon name="youtube" /> {t('project.social.youtube')}</div>
                  </div>
                </EditorSectionCard>
              </>
            ) : null}

            {editorTab === 'builder' ? (
              builderWorkspace
            ) : null}

            {editorTab === 'tasks' ? (
              <EditorSectionCard
                title={t('project.sections.tasks')}
                actions={
                  <button className="av-primary-button" type="button" onClick={() => setTaskModalState({ mode: 'create' })}>
                    {t('project.tasks.actions.add')}
                  </button>
                }
              >
                <div className="av-tasks-panel">
                  {taskMutationError ? <div className="av-inline-error">{taskMutationError}</div> : null}
                  {detail.tasks.length === 0 ? (
                    <EmptyState title={t('project.tasks.title')} description={t('project.tasks.description')} />
                  ) : (
                    <ul className="av-compact-list">
                      {detail.tasks.map((task) => (
                        <li key={task.id} className="av-compact-list__item">
                          <div>
                            <strong>{task.title}</strong>
                            <span>{task.dueAt ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(task.dueAt)) : t('common.noDeadline')}</span>
                          </div>
                          <div className="av-inline-metadata">
                            <StatusBadge label={t(`enums.taskStatus.${task.status}`)} tone="neutral" />
                            <button className="av-inline-link-button" type="button" onClick={() => setTaskModalState({ mode: 'edit', taskId: task.id })}>
                              {t('tasks.card.actions.edit')}
                            </button>
                            <button className="av-inline-link-button" type="button" onClick={() => void handleDeleteProjectTask(task.id)}>
                              {t('tasks.card.actions.delete')}
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </EditorSectionCard>
              ) : null}
            </>
          }
          rail={
            editorTab === 'builder' ? undefined : (
            <>
              {isRuntimeBusy ? (
                <EditorSectionCard title={t('project.sections.runtimeActivity')} actions={<OverflowMenuButton />}>
                <div className="av-summary-stack">
                  <ValuePair
                    label={t('project.fields.runtimeOperation')}
                    value={runtimeActionKind ? t(`project.runtime.operationKinds.${runtimeActionKind === 'install' ? 'provision' : 'update'}`) : runtimeOperationKindLabel ?? t('common.notSet')}
                  />
                  <ValuePair label={t('project.fields.runtimeStep')} value={runtimeStepLabel ?? runtimeMessage} />
                  <ValuePair label={t('project.fields.runtimeStartedAt')} value={runtimeStartedAt} />
                  <div className={styles.runtimeProgress}>
                    <div className={styles.runtimeProgressHeader}>
                      <Spinner size="small" label={runtimeStepLabel ?? runtimeMessage} />
                      <span className="av-summary-label">{t('project.summary.runtimeProgress')}</span>
                    </div>
                    <ProgressBar value={runtimeProgressValue} />
                  </div>
                  <div className="av-readonly-text-block">{runtimeMessage}</div>
                </div>
              </EditorSectionCard>
              ) : null}
              <EditorSectionCard title={t('project.sections.wordpressAdmin')} actions={<OverflowMenuButton />}>
                <div className="av-summary-stack">
                  <ValuePair
                    label={t('project.summary.wordpressAdminDefaultUsername')}
                    value={workspaceSettings.localEnvironment.wordpressAdminUsername}
                  />
                  <ValuePair
                    label={t('project.summary.wordpressAdminProjectPassword')}
                    value={detail.wordpressAdminPasswordOverrideSet ? t('common.saved') : t('common.notSet')}
                  />
                  <ValuePair
                    label={t('project.summary.wordpressAdminDefaultPassword')}
                    value={workspaceSettings.localEnvironment.wordpressAdminPasswordSet ? t('common.saved') : t('common.notSet')}
                  />
                  <label className="av-form-field">
                    <span>{t('project.fields.wordpressAdminUsername')}</span>
                    <TextField
                      value={wordpressAdminDraft.username}
                      placeholder={workspaceSettings.localEnvironment.wordpressAdminUsername}
                      onValueChange={(value) => setWordpressAdminDraft((current) => ({ ...current, username: value }))}
                    />
                  </label>
                  <label className="av-form-field">
                    <span>{t('project.fields.wordpressAdminPassword')}</span>
                    <TextField
                      type="password"
                      value={wordpressAdminDraft.password}
                      placeholder="••••••"
                      onValueChange={(value) =>
                        setWordpressAdminDraft((current) => ({
                          ...current,
                          password: value
                        }))
                      }
                    />
                  </label>
                  <div className="av-inline-metadata">
                    <button
                      className="av-primary-button"
                      type="button"
                      disabled={isSavingWordpressAdmin}
                      onClick={() => void handleSaveWordPressAdminCredentials()}
                    >
                      {t('project.actions.saveCredentials')}
                    </button>
                    <button
                      className="av-secondary-button"
                      type="button"
                      disabled={!detail.wordpressAdminPasswordOverrideSet || isSavingWordpressAdmin}
                      onClick={() => void handleClearWordPressAdminPasswordOverride()}
                    >
                      {t('project.actions.clearProjectPassword')}
                    </button>
                  </div>
                  {wordpressAdminError ? <div className="av-inline-error">{wordpressAdminError}</div> : null}
                </div>
              </EditorSectionCard>
              <EditorSectionCard title={t('project.sections.summary')} actions={<OverflowMenuButton />}>
                <div className="av-summary-stack">
                <ValuePair label={t('project.summary.status')} value={model.statusLabel} />
                <ValuePair label={t('project.summary.stage')} value={model.stageLabel} />
                <ValuePair label={t('project.summary.deployment')} value={model.deploymentLabel} />
                <ValuePair label={t('project.summary.runtimeMessage')} value={runtimeMessage} />
                <div>
                  <span className="av-summary-label">{t('project.summary.progress')}</span>
                  <ProgressBar value={detail.project.progressPercent ?? 0} />
                </div>
                <div>
                  <span className="av-summary-label">{t('project.summary.recentChanges')}</span>
                  <ul className="av-summary-list">
                    {detail.timeline.map((item) => (
                      <li key={item.id}>
                        <strong>{t(`project.timeline.${item.kind}`)}</strong>
                        <span>{new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(item.occurredAt))}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                </div>
              </EditorSectionCard>
            </>
            )
          }
        />
      </EditorPageLayout>
      {taskModalState?.mode === 'create' ? (
        <TaskEditModal
          isOpen
          mode="create"
          task={newProjectTaskDraft}
          statusOptions={taskStatusOptions}
          projectOptions={taskProjectOptions}
          onClose={() => setTaskModalState(null)}
          onSave={handleCreateProjectTask}
          strings={{
            close: t('common.close'),
            title: t('tasks.modal.createTitle'),
            save: t('common.applyChanges'),
            cancel: t('common.cancel'),
            fieldTitle: t('quickAdd.fields.title'),
            fieldDescription: t('quickAdd.fields.description'),
            fieldStatus: t('tasks.fields.status'),
            fieldProject: t('quickAdd.fields.project'),
            fieldDueAt: t('quickAdd.fields.dueAt'),
            validationTitleRequired: t('quickAdd.validation.titleRequired')
          }}
        />
      ) : null}
      {taskModalState?.mode === 'edit' && activeTaskRecord ? (
        <TaskEditModal
          isOpen
          task={activeTaskRecord}
          statusOptions={taskStatusOptions}
          projectOptions={taskProjectOptions}
          onClose={() => setTaskModalState(null)}
          onSave={handleUpdateProjectTask}
          strings={{
            close: t('common.close'),
            title: t('tasks.modal.editTitle'),
            save: t('common.applyChanges'),
            cancel: t('common.cancel'),
            fieldTitle: t('quickAdd.fields.title'),
            fieldDescription: t('quickAdd.fields.description'),
            fieldStatus: t('tasks.fields.status'),
            fieldProject: t('quickAdd.fields.project'),
            fieldDueAt: t('quickAdd.fields.dueAt'),
            validationTitleRequired: t('quickAdd.validation.titleRequired')
          }}
        />
      ) : null}
      <ProjectResourcesModal
        isOpen={isResourcesModalOpen}
        projectId={currentDetail.project.id}
        projectName={currentDetail.project.name}
        customerId={currentDetail.project.customerId}
        initialAssets={currentDetail.assets}
        onClose={() => setIsResourcesModalOpen(false)}
        onAssetsChanged={refreshProjectDetail}
      />
    </>
  );
}
